import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import DateSearch from "../components/DateSearch";
import TableHeaderFilter from "../components/TableHeaderfilter";
import SearchableDropdown from "../components/SearchableDropdown";
import { SERVER_URL } from "../config";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Bars3Icon, PlusIcon, PencilIcon } from "@heroicons/react/24/outline";

/* ── shared input style ────────────────────────────────────────────────────── */
const inputStyle =
    "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

/* ── helpers ────────────────────────────────────────────────────────────────── */
const todayStr = () => new Date().toISOString().split("T")[0];

/* ── stat card pills ─────────────────────────────────────────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </div>
);

/* ── stat tile (large) ──────────────────────────────────────────────────────── */
const StatTile = ({ label, value, colorClass, borderClass, textClass, numClass }) => (
    <div className={`flex flex-col gap-1 px-5 py-4 rounded-xl border ${borderClass} ${colorClass}`}
        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
        <p className={`text-[10px] font-bold uppercase tracking-widest ${textClass}`}>{label}</p>
        <p className={`text-3xl font-extrabold ${numClass}`}>{value}</p>
    </div>
);

/* ── priority badge ─────────────────────────────────────────────────────────── */
const PriorityBadge = ({ priority }) => {
    const map = {
        high:   "bg-rose-50 text-rose-700 border border-rose-200",
        medium: "bg-amber-50 text-amber-700 border border-amber-200",
        low:    "bg-emerald-50 text-emerald-700 border border-emerald-200",
    };
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[priority] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
            {priority}
        </span>
    );
};

/* ── status badge ───────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold
        ${status === "completed"
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
        {status === "completed" ? "Completed" : "Pending"}
    </span>
);

/* ════════════════════════════════════════════════════════════════════════════ */
const TaskManagementPage = () => {

    const [sidebarOpen, setSidebarOpen] = useState(false);

    /* ---------- raw data from API ---------- */
    const [tasks,     setTasks]     = useState([]);
    const [employees, setEmployees] = useState([]);

    /* ---------- task-type tab ---------- */
    const [taskType, setTaskType] = useState("daily");

    /* ---------- date range (from DateSearch) ---------- */
    const [dateRange, setDateRange] = useState({ start: "", end: "" });

    /* ---------- table column filters ---------- */
    const [employeeFilter, setEmployeeFilter] = useState([]);
    const [taskFilter,     setTaskFilter]     = useState([]);
    const [typeFilter,     setTypeFilter]     = useState([]);

    /* ---------- modal ---------- */
    const [openModal, setOpenModal] = useState(false);
    const [editMode,  setEditMode]  = useState(false);
    const [toast,     setToast]     = useState({ show: false, type: "", message: "" });
    const [formData,  setFormData]  = useState({
        id: "", employee_id: "", task_title: "", task_description: "",
        priority: "medium", start_date: "", end_date: ""
    });

    const employeeData    = JSON.parse(localStorage.getItem("employee"));
    const loggedEmployeeId = employeeData?.employee_id;

    /* ---------- fetch ---------- */
    const fetchEmployees = async () => {
        try {
            const res = await axios.get(`${SERVER_URL}/Rahul/get_employee.php`);
            setEmployees(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchTasks = async () => {
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/TaskHistory.php`, {
                task_type:  taskType,
                start_date: dateRange.start,
                end_date:   dateRange.end,
            });
            const data = res.data;
            setTasks(data.tasks || []);
        } catch (err) { console.error(err); }
    };

    useEffect(() => { fetchEmployees(); }, []);
    useEffect(() => { fetchTasks(); }, [taskType, dateRange]);

    /* ---------- date search ---------- */
    const handleDateSearch = (start, end) => setDateRange({ start, end });

    const today = todayStr();

    const dateFilteredTasks = useMemo(() => {
        return tasks.filter(task => {
            if (taskType === "daily") {
                if (dateRange.start && dateRange.end) {
                    return (
                        task.task_type === "daily" &&
                        task.start_date >= dateRange.start &&
                        task.start_date <= dateRange.end
                    );
                } else {
                    return (
                        task.task_type === "daily" &&
                        task.start_date <= today &&
                        task.end_date   >= today
                    );
                }
            } else {
                return task.task_type === taskType;
            }
        });
    }, [tasks, taskType, dateRange, today]);

    /* Apply column-level filters on top */
    const filteredTasks = useMemo(() => dateFilteredTasks.filter(task => {
        if (employeeFilter.length && !employeeFilter.includes(task.employee_name)) return false;
        if (taskFilter.length     && !taskFilter.includes(task.title))             return false;
        if (typeFilter.length     && !typeFilter.includes(task.task_type))         return false;
        return true;
    }), [dateFilteredTasks, employeeFilter, taskFilter, typeFilter]);

    /* Summary tiles */
    const filteredSummary = useMemo(() => {
        const total     = filteredTasks.length;
        const completed = filteredTasks.filter(t => t.status === "completed").length;
        return { total, completed, pending: total - completed };
    }, [filteredTasks]);

    /* Graph */
    const graphData = useMemo(() => {
        const base = employeeFilter.length
            ? filteredTasks.filter(t => employeeFilter.includes(t.employee_name))
            : filteredTasks;

        const grouped = {};
        base.forEach(task => {
            const name = task.employee_name || "Unknown";
            if (!grouped[name]) grouped[name] = { name, Assigned: 0, Completed: 0, Pending: 0 };
            grouped[name].Assigned += 1;
            task.status === "completed" ? grouped[name].Completed++ : grouped[name].Pending++;
        });
        return Object.values(grouped);
    }, [filteredTasks, employeeFilter]);

    /* ---------- modal helpers ---------- */
    const openAssignModal = () => {
        setEditMode(false);
        setFormData({ id: "", employee_id: "", task_title: "", task_description: "", priority: "medium", start_date: "", end_date: "" });
        setOpenModal(true);
    };

    const handleRowClick = (task) => {
        setEditMode(true);
        setFormData({
            id: task.task_id, employee_id: task.assign_to, task_title: task.title,
            task_description: task.description, priority: task.priority,
            start_date: task.start_date, end_date: task.end_date,
        });
        setOpenModal(true);
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    /* ---------- save ---------- */
    const saveTask = async () => {
        try {
            const empData  = JSON.parse(localStorage.getItem("employee"));
            const payload  = {
                task_id: formData.id, assign_to: formData.employee_id, title: formData.task_title,
                description: formData.task_description, priority: formData.priority,
                start_date: formData.start_date, end_date: formData.end_date,
                assigner_id: empData?.employee_id,
            };
            const endpoint = editMode ? "Update_task.php" : "insertNewTasks.php";
            const res      = await axios.post(`${SERVER_URL}/Rahul/${endpoint}`, payload);
            if (res.data.status === "error") {
                setToast({ show: true, type: "error", message: res.data.message });
                return;
            }
            setToast({ show: true, type: "success", message: res.data.message });
            setOpenModal(false);
            fetchTasks();
        } catch (err) {
            setToast({ show: true, type: "error", message: "Server error. Please try again." });
            console.error(err);
        }
    };

    useEffect(() => {
        if (toast.show) {
            const t = setTimeout(() => setToast({ ...toast, show: false }), 3000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    useEffect(() => {
        document.body.style.overflow = openModal ? "hidden" : "auto";
        return () => { document.body.style.overflow = "auto"; };
    }, [openModal]);

    /* ---------- filter dropdown options ---------- */
    const employeeOptionsDropdown = employees.map(emp => ({ label: `${emp.name} (${emp.id})`, value: emp.id }));
    const employeeOptions = [...new Set(filteredTasks.map(t => t.employee_name))].map(emp => ({ label: emp, value: emp }));
    const taskOptions     = [...new Set(filteredTasks.map(t => t.title))].map(t => ({ label: t, value: t }));
    const typeOptions     = [
        { label: "Daily",   value: "daily" },
        { label: "Monthly", value: "monthly" },
        { label: "Yearly",  value: "yearly" },
    ];

    /* ── tab styles ── */
    const TAB_STYLES = {
        daily:   { active: "bg-blue-600 text-white border-blue-600",         idle: "text-slate-600 border-slate-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300" },
        monthly: { active: "bg-amber-500 text-white border-amber-500",       idle: "text-slate-600 border-slate-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300" },
        yearly:  { active: "bg-emerald-600 text-white border-emerald-600",   idle: "text-slate-600 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300" },
    };

    /* ════════════════════════════════════════════════════════════════════════ */
    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                * { box-sizing: border-box; }
                body { font-family: 'Inter', sans-serif; }
                .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .att-scrollbar::-webkit-scrollbar-track { background: rgba(226,232,240,0.6); border-radius:10px; }
                .att-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius:10px; }
                .att-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
            `}</style>

            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                {/* Toast */}
                {toast.show && (
                    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999]">
                        <div className={`px-6 py-3 rounded-xl shadow-2xl text-sm font-bold
                            ${toast.type === "success" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"}`}>
                            {toast.message}
                        </div>
                    </div>
                )}

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Task Monitoring
                                </h1>
                            </div>
                        </div>
                        <button
                            onClick={openAssignModal}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white border-0 transition-all hover:opacity-90"
                            style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                        >
                            <PlusIcon className="h-4 w-4" />
                            Assign Task
                        </button>
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 flex flex-col gap-4 p-5">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">

                            {/* Summary pills + Tabs */}
                            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                                {/* Task-type tabs */}
                                {["daily", "monthly", "yearly"].map((type) => {
                                    const isActive = taskType === type;
                                    const st = TAB_STYLES[type];
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => {
                                                setTaskType(type);
                                                if (type !== "daily") setDateRange({ start: "", end: "" });
                                            }}
                                            className={`px-4 py-1.5 rounded-lg text-xs font-bold border capitalize transition-all duration-200 ${isActive ? st.active : st.idle}`}
                                        >
                                            {type}
                                        </button>
                                    );
                                })}

                                <div className="w-px h-5 bg-slate-200 mx-1" />

                                <StatCard label="Total"     value={filteredSummary.total}     colorClass="bg-blue-50 text-blue-700 border border-blue-200"       dotClass="bg-blue-500" />
                                <StatCard label="Completed" value={filteredSummary.completed} colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200" dotClass="bg-emerald-500" />
                                <StatCard label="Pending"   value={filteredSummary.pending}   colorClass="bg-rose-50 text-rose-700 border border-rose-200"         dotClass="bg-rose-500" />
                            </div>

                            {/* Right: date search (daily only) */}
                            {taskType === "daily" && (
                                <DateSearch onSearch={handleDateSearch} />
                            )}
                        </div>

                        {/* Context label */}
                        <p className="text-xs text-slate-400 mt-2">
                            {taskType === "daily"
                                ? dateRange.start && dateRange.end
                                    ? `Showing daily tasks with start date between ${dateRange.start} and ${dateRange.end}`
                                    : `Showing daily tasks active today (${today})`
                                : `Showing all ${taskType} tasks`
                            }
                        </p>
                    </div>

                    {/* ── STAT TILES ── */}
                    <div className="grid grid-cols-3 gap-4">
                        <StatTile
                            label="Total Tasks" value={filteredSummary.total}
                            colorClass="bg-blue-50"    borderClass="border-blue-200"
                            textClass="text-blue-600"   numClass="text-blue-800"
                        />
                        <StatTile
                            label="Completed" value={filteredSummary.completed}
                            colorClass="bg-emerald-50"  borderClass="border-emerald-200"
                            textClass="text-emerald-600" numClass="text-emerald-800"
                        />
                        <StatTile
                            label="Pending" value={filteredSummary.pending}
                            colorClass="bg-rose-50"    borderClass="border-rose-200"
                            textClass="text-rose-600"   numClass="text-rose-800"
                        />
                    </div>

                    {/* ── BAR CHART ── */}
                    <div className="rounded-xl p-6 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-1 h-5 rounded-full bg-blue-700" />
                            <h3 className="text-sm font-bold text-slate-800">Assigned vs Completed</h3>
                        </div>
                        {employeeFilter.length > 0 && (
                            <p className="text-xs text-slate-400 mb-3 ml-3">— {employeeFilter.join(", ")}</p>
                        )}
                        {graphData.length === 0 ? (
                            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No data available</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={graphData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }} barCategoryGap="30%" barGap={4}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(226,232,240,0.8)" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} tickLine={false} axisLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} tickLine={false} axisLine={false} />
                                    <Tooltip contentStyle={{
                                        background: "#ffffff", border: "1px solid rgba(226,232,240,1)",
                                        borderRadius: 10, fontSize: 12, color: "#334155",
                                        boxShadow: "0 4px 12px rgba(15,23,42,0.1)"
                                    }} />
                                    <Legend wrapperStyle={{ fontSize: 11, color: "#64748b", fontWeight: 600 }} />
                                    <Bar dataKey="Assigned"  fill="#3b82f6" radius={[5,5,0,0]} />
                                    <Bar dataKey="Completed" fill="#10b981" radius={[5,5,0,0]} />
                                    <Bar dataKey="Pending"   fill="#f43f5e" radius={[5,5,0,0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* ── TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[420px] att-scrollbar">
                            <table className="min-w-full text-sm table-fixed">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        {[
                                            { width: "w-[16%]", content: <TableHeaderFilter label="Employee" options={employeeOptions} selectedValues={employeeFilter} onChange={setEmployeeFilter} /> },
                                            { width: "w-[20%]", content: <TableHeaderFilter label="Task"     options={taskOptions}     selectedValues={taskFilter}     onChange={setTaskFilter} /> },
                                            { width: "w-[12%]", content: <TableHeaderFilter label="Type"     options={typeOptions}     selectedValues={typeFilter}     onChange={setTypeFilter} /> },
                                            { width: "w-[12%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Priority</span> },
                                            { width: "w-[12%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Start Date</span> },
                                            { width: "w-[12%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">End Date</span> },
                                            { width: "w-[10%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Result</span> },
                                            { width: "w-[12%]", content: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span> },
                                        ].map((col, i) => (
                                            <th key={i} className={`p-3 text-left ${col.width} border-b border-slate-200`}>
                                                {col.content}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTasks.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="text-center p-8 text-slate-400 text-sm">No tasks found.</td>
                                        </tr>
                                    ) : (
                                        filteredTasks.map((task) => (
                                            <tr
                                                key={task.task_id}
                                                onClick={() => handleRowClick(task)}
                                                className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60"
                                            >
                                                <td className="p-3 text-slate-800 truncate text-xs font-medium">{task.employee_name}</td>
                                                <td className="p-3 text-slate-800 truncate text-xs">{task.title}</td>
                                                <td className="p-3 text-slate-500 capitalize text-xs">{task.task_type}</td>
                                                <td className="p-3"><PriorityBadge priority={task.priority} /></td>
                                                <td className="p-3 text-slate-500 text-xs">{task.start_date}</td>
                                                <td className="p-3 text-slate-500 text-xs">{task.end_date}</td>
                                                <td className="p-3 text-slate-400 text-xs">{task.result || "—"}</td>
                                                <td className="p-3"><StatusBadge status={task.status} /></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MODAL ── */}
            {openModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center"
                    style={{ background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)" }}>
                    <div className="w-[90%] md:w-[520px] rounded-2xl p-6 space-y-5 bg-white border border-slate-200"
                        style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.2)" }}>

                        <div className="flex items-center gap-2.5 border-b border-slate-100 pb-4">
                            <div className="w-1 h-6 rounded-full"
                                style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                {editMode
                                    ? <><PencilIcon className="h-5 w-5 text-blue-600" /> Edit Task</>
                                    : <><PlusIcon   className="h-5 w-5 text-blue-600" /> Assign Task</>
                                }
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="w-full">
                                <SearchableDropdown
                                    label="Employee"
                                    options={employeeOptionsDropdown}
                                    value={formData.employee_id}
                                    placeholder="Select Employee"
                                    onChange={(val) => setFormData({ ...formData, employee_id: val })}
                                />
                            </div>

                            <select name="priority" value={formData.priority} onChange={handleChange} className={inputStyle}>
                                <option value="low">Low Priority</option>
                                <option value="medium">Medium Priority</option>
                                <option value="high">High Priority</option>
                            </select>

                            <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} className={inputStyle} />
                            <input type="date" name="end_date"   value={formData.end_date}   onChange={handleChange} className={inputStyle} />

                            <input
                                name="task_title" value={formData.task_title} onChange={handleChange}
                                placeholder="Task Title" className={inputStyle}
                            />

                            <textarea
                                name="task_description" value={formData.task_description} onChange={handleChange}
                                placeholder="Task Description"
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 h-20 resize-none text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors"
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-1">
                            <button
                                onClick={() => setOpenModal(false)}
                                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveTask}
                                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                                style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                            >
                                {editMode && <PencilIcon className="h-4 w-4" />}
                                {editMode ? "Update" : "Assign"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TaskManagementPage;